import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, state } from 'lit/decorators.js';
import componentStyles from './se-lottie.css.lit';
import lottie, { AnimationItem } from 'lottie-web';
// import { IndividualSlotsController } from '../../controllers/individual-slots-controller';
import { SlotController } from '../../controllers/slot-controller';
import '../se-container/se-container';
import '../se-background/se-background';

/**
 * The SeLottie component
 * @element se-lottie
 */
@customElement('se-lottie')
export class SeLottie extends OutlineElement {
  slots = new SlotController(this, false);
  static styles: CSSResultGroup = [componentStyles];
  animations = new Map();

  @state() hasSlot1: boolean;
  @state() hasSlot2: boolean;
  @state() hasSlot3: boolean;
  @state() hasSlot4: boolean;
  @state() hasSlot5: boolean;
  @state() hasSlot6: boolean;
  @state() hasSlot7: boolean;
  @state() hasHeadingSlot: boolean;
  @state() hasDescription: boolean;
  @state() hasSlot8: boolean;
  @state() hasCta: boolean;

  async loadAnimationFile(container: HTMLElement, filename: string) {
    // Fetch from different locations for storybook vs. drupal
    // Drupal
    const response = await fetch(
      `/themes/custom/outline_frontend/dist/lottie-animations/${filename}.json`
    );
    const animationFile = response.json();
    // Storybook
    // const animationFile = require(`./lottie-animations/${filename}.json`);

    return lottie.loadAnimation({
      container: container, // the dom element that will contain the animation
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: await animationFile, // the path to the animation json
    });
  }

  async prepareAnimation() {
    setTimeout(async () => {
      const refs = this.shadowRoot?.querySelectorAll(`[data-animation]`);

      if (refs) {
        // Using 'for' instead of forEach, because 'for' would respect sequential order, while forEach would run in parallel.
        // https://stackoverflow.com/a/69901794
        for (const ref of refs!) {
          const refElement = ref as HTMLElement;
          const name = refElement.dataset.animation as string;
          const animation = await this.loadAnimationFile(refElement, name);
          this.animations.set(name, animation);
        }

        const refObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio > 0) {
              this.startAnimation();
            }
          });
        });

        refObserver.observe(refs![0]);
      }
    }, 0);
  }

  startAnimation() {
    let delay = 0;
    let skipAnimation = true;

    // support prefers-reduced-motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    skipAnimation = mediaQuery.matches;
    mediaQuery.addEventListener('change', () => {
      skipAnimation = mediaQuery.matches;
    });

    if (skipAnimation) {
      this.animations.forEach((animation: AnimationItem) => {
        // display last frame of animation
        animation.goToAndStop(animation.totalFrames, true);
      });
    } else {
      // Run animations sequentially
      this.animations.forEach((animation: AnimationItem) => {
        setTimeout(() => {
          animation.play();
        }, delay);

        delay += (animation.getDuration() * 1000) / 2;
      });
    }
  }

  revealAnimation() {
    setTimeout(() => {
      const elementsToReveal = this.shadowRoot?.querySelectorAll('.reveal');
      const revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio > 0) {
            entry.target.classList.add('revealing');
          } else {
            entry.target.classList.remove('revealing');
          }
        });
      });

      elementsToReveal?.forEach(element => {
        revealObserver.observe(element);
      });
    }, 0);
  }

  firstUpdated() {
    this.hasSlot1 = this.slots.test('slot1');
    this.hasSlot2 = this.slots.test('slot2');
    this.hasSlot3 = this.slots.test('slot3');
    this.hasSlot4 = this.slots.test('slot4');
    this.hasSlot5 = this.slots.test('slot5');
    this.hasSlot6 = this.slots.test('slot6');
    this.hasSlot7 = this.slots.test('slot7');
    this.hasHeadingSlot = this.slots.test('heading');
    this.hasDescription = this.slots.test('description');
    this.hasSlot8 = this.slots.test('slot8');
    this.hasCta = this.slots.test('cta');
    this.prepareAnimation();
    this.revealAnimation();
  }

  displaySlot(
    animationName: string,
    slotName: string,
    slotTest: boolean
  ): TemplateResult {
    if (slotTest) {
      return html`
        <div class="se-lottie__cell">
          <div aria-hidden="true" data-animation="${animationName}"></div>
          <slot name="${slotName}"></slot>
        </div>
      `;
    } else {
      return html`${nothing}`;
    }
  }

  render(): TemplateResult {
    return html`
      <se-background
        alignment="center"
        color="primary-2"
        variant="wave"
        curve-width="75"
        full-bleed
      >
        <se-container class="reveal">
          ${this.hasHeadingSlot || this.hasDescription
            ? html`<div class="se-lottie__top">
                ${this.hasHeadingSlot
                  ? html`<slot name="heading"></slot>`
                  : nothing}
                ${this.hasDescription
                  ? html`<slot name="description"></slot>`
                  : nothing}
              </div>`
            : nothing}
          <se-grid
            col-gap="2rem"
            row-gap="2rem"
            item-min-width="18rem"
            columns="4"
          >
            ${this.displaySlot('circle1', 'slot1', this.hasSlot1)}
            ${this.displaySlot('circle2', 'slot2', this.hasSlot2)}
            ${this.displaySlot('circle3', 'slot3', this.hasSlot3)}
            ${this.displaySlot('circle4', 'slot4', this.hasSlot4)}
            ${this.displaySlot('circle5', 'slot5', this.hasSlot5)}
            ${this.displaySlot('circle6', 'slot6', this.hasSlot6)}
            ${this.displaySlot('circle7', 'slot7', this.hasSlot7)}
            ${this.hasSlot8 || this.hasCta
              ? html`
                  <div class="se-lottie__cell">
                    <div class="se-lottie__cta">
                      ${this.hasSlot8
                        ? html`<slot name="slot8"></slot>`
                        : nothing}
                      ${this.hasCta ? html`<slot name="cta"></slot>` : nothing}
                    </div>
                  </div>
                `
              : nothing}
          </se-grid>
        </se-container>
      </se-background>
    `;
  }
}
